// material-ui
import { Box, FormControl, IconButton, InputAdornment, OutlinedInput, useMediaQuery } from '@mui/material';
import { PatternFormat } from 'react-number-format';
import { App } from 'antd';

// assets
import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useGlobalSearchMutation } from 'store/features/process/processApi';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import deactivated from 'assets/images/icons/deactivated.svg';

const SearchBox = (props) => {
	const { value, ...othersProps } = props;

	return (
		<OutlinedInput
			{...othersProps}
			value={value}
			size="small"
			sx={{
				color: 'rgb(209, 213, 219)',
				'& input:-webkit-autofill': {
					WebkitTextFillColor: 'rgb(209, 213, 219) !important',
					transition: 'background-color 5000s ease-in-out 0s',
					WebkitBoxShadow: '0 0 0px 1000px #171e2b inset !important',
				},

				'& input:-webkit-autofill:hover': {
					WebkitTextFillColor: 'rgb(209, 213, 219) !important',
				},

				'& input:-webkit-autofill:focus': {
					WebkitTextFillColor: 'rgb(209, 213, 219) !important',
				},

				'&:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: 'rgba(255, 255, 255, 0.2)',
				},
				'& .MuiOutlinedInput-notchedOutline': {
					borderColor: 'rgba(255, 255, 255, 0.2)',
				},
			}}
			startAdornment={
				<InputAdornment position="start" sx={{ border: 'none', mr: -0.5, color: 'rgb(209, 213, 219)' }}>
					<SearchOutlined />
				</InputAdornment>
			}
		/>
	);
};

const Search = ({ id, compact = false }) => {
	const { message: messageApi } = App.useApp();
	const [GlobalSearch, { data }] = useGlobalSearchMutation();

	const userId = useSelector((state) => state.auth.userId);
	const creatorOrganizationId = useSelector((state) => state.profile.profileData.creatorOrganizationId);
	const navigate = useNavigate();
	const location = useLocation();
	const matchesMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

	const [floatValue, setFloatValue] = useState('');
	const [formattedValue, setFormattedValue] = useState('');
	const [value, setValue] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const inputRef = useRef(null);

	const handleSubmit = (event) => {
		event.preventDefault();

		if (value?.length === 8) {
			GlobalSearch({
				companyCode: creatorOrganizationId,
				userId,
				lynUniqueId: formattedValue,
			});
		}
	};

	useEffect(() => {
		if (data?.status === 'SUCCESS' && data?.id) {
			navigate('/process-form', { state: { stageId: data.id, processCode: data.processCode } });
		} else if (data?.status === 'FAILURE') {
			messageApi.error({
				content: 'Invalid entry or you do not have permission to this case.',
				icon: <img src={deactivated} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
			});
		}
	}, [data, navigate]);

	useEffect(() => {
		if (compact) return;
		if (location.pathname !== '/process-form') {
			setFloatValue('');
			setFormattedValue('');
			setValue('');
		}
	}, [location.pathname, compact]);

	useEffect(() => {
		if (!compact || !searchOpen) return;
		const focusTimer = window.setTimeout(() => {
			const domInput = document.getElementById(id);
			if (domInput?.focus) domInput.focus();
			if (inputRef.current?.focus) inputRef.current.focus();
			if (inputRef.current?.input?.focus) inputRef.current.input.focus();
		}, 220);

		return () => window.clearTimeout(focusTimer);
	}, [compact, searchOpen, id]);

	const searchInput = (
		<FormControl
			sx={{
				width: compact ? 450 : { xs: '100%', md: 350, lg: 600 },
				backgroundColor: '#171e2b',
				borderRadius: '6px',
			}}
		>
			<PatternFormat
				id={id}
				format="####-####"
				mask="#"
				customInput={SearchBox}
				autoFocus={compact && searchOpen}
				value={floatValue}
				onValueChange={(values) => {
					setFormattedValue(values.formattedValue);
					setValue(values.value);
					setFloatValue(values.floatValue);
				}}
				placeholder="Search modules, cases, documents..."
				valueIsNumericString={true}
				getInputRef={(element) => {
					inputRef.current = element;
				}}
				onBlur={() => {
					if (!value) setSearchOpen(false);
				}}
				onKeyDown={(event) => {
					if (event.key === 'Enter') {
						handleSubmit(event);
					}
				}}
			/>
		</FormControl>
	);

	if (compact) {
		if (matchesMobile) return null;

		return (
			<Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
				{!searchOpen ? (
					<IconButton
						disableRipple
						aria-label="toggle search"
						onClick={() => {
							setSearchOpen(true);
							window.setTimeout(() => {
								const domInput = document.getElementById(id);
								if (domInput?.focus) domInput.focus();
							}, 10);
						}}
						sx={{
							color: 'brandDark.contrastText',
							'&:hover': { color: 'secondary.lighter' },
							width: 'auto',
							height: 'auto',
							padding: '0 12px',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<SearchOutlined />
					</IconButton>
				) : null}
				<Box
					sx={{
						mr: 1,
						overflow: 'hidden',
						transition: 'width 0.28s ease, opacity 0.2s ease',
						width: searchOpen ? 450 : 0,
						opacity: searchOpen ? 1 : 0,
						pointerEvents: searchOpen ? 'auto' : 'none',
					}}
				>
					<form onSubmit={handleSubmit}>{searchInput}</form>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', textAlign: 'center', ml: { xs: 0, md: 1 } }}>
			<form onSubmit={handleSubmit}>{searchInput}</form>
		</Box>
	);
};

export default Search;
